import { addMonths, format, startOfDay } from "date-fns";

// Local storage of in-progress welcome-wizard preferences, keyed per email.
// We persist here so that:
//   1. A reload during the wizard doesn't lose answers
//   2. After signup we can mirror everything into the user's profile row
//
// The wizard ALSO writes the columns that exist on `email_captures`
// (preferred_area, preferred_bedrooms, preferred_budget) live, so the
// email-alert system has the latest filters even before the user creates
// an account.

const KEY = "mst_welcome_prefs_v1";

export interface WelcomePrefs {
  email: string;
  captureId?: string;
  preferred_areas: string[];
  preferred_bedrooms?: number;
  preferred_budget_min?: number;
  preferred_budget_max?: number;
  preferred_types: string[];
  preferred_amenities: string[];
  move_in_window?: "this_month" | "1_3_months" | "3_6_months" | "exploring";
  move_in_date?: string; // YYYY-MM-DD derived from window
  move_out_date?: string;
}

export const loadWelcomePrefs = (): WelcomePrefs | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as WelcomePrefs;
  } catch {
    return null;
  }
};

export const saveWelcomePrefs = (prefs: WelcomePrefs) => {
  if (typeof window === "undefined") return;
  try {
    // Never clobber a previously captured email (and its capture row) with an
    // empty one, callers like the concierge save prefs without knowing the
    // visitor's email, and the wizard can collect it at the signup step.
    const next = { ...prefs };
    if (!next.email) {
      const existing = loadWelcomePrefs();
      if (existing?.email) {
        next.email = existing.email;
        if (!next.captureId && existing.captureId) next.captureId = existing.captureId;
      }
    }
    window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
};

export const updateWelcomePrefs = (patch: Partial<WelcomePrefs>): WelcomePrefs => {
  const current = loadWelcomePrefs() ?? {
    email: "",
    preferred_areas: [],
    preferred_types: [],
    preferred_amenities: [],
  };
  const next = { ...current, ...patch } as WelcomePrefs;
  saveWelcomePrefs(next);
  return next;
};

export const clearWelcomePrefs = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};

// Derive concrete move_in_date / move_out_date from a friendly window choice.
// Local calendar dates via date-fns: toISOString() would shift to tomorrow
// after ~18:00 in Costa Rica, and raw setMonth() overflows Jan 31 → Mar 3.
export const windowToDates = (
  win: WelcomePrefs["move_in_window"],
): { move_in_date?: string; move_out_date?: string } => {
  const today = startOfDay(new Date());
  const iso = (d: Date) => format(d, "yyyy-MM-dd");
  switch (win) {
    case "this_month":
      return { move_in_date: iso(today), move_out_date: iso(addMonths(today, 3)) };
    case "1_3_months": {
      const inD = addMonths(today, 1);
      return { move_in_date: iso(inD), move_out_date: iso(addMonths(inD, 3)) };
    }
    case "3_6_months": {
      const inD = addMonths(today, 3);
      return { move_in_date: iso(inD), move_out_date: iso(addMonths(inD, 6)) };
    }
    default:
      return {};
  }
};
